import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { firebaseAuth } from '../../../../lib/firebase';
import { loadRememberedSocialAccount, rememberSocialAccount, resolveSocialAccount } from '../../../../lib/socialAccountMemory';
import { fetchPortalMe } from '../portalApi';
import { PortalShell } from '../PortalShell';
import { socialApi } from './socialApi';

// KyckSocial — the shared page frame: shell, the page's own title and the location picker.
// COSMETIC GUARD ONLY. Whether the viewer may do anything is decided by the
// server on every call; this just picks which screen to draw. A club outside the
// selection is a 404 from the API, never an empty list.

export type SocialTab = 'posts' | 'inbox' | 'profile';

// Each KyckSocial page is its own left-nav destination, so the page names itself.
const TITLES: Record<SocialTab, string> = {
  posts: 'Posting',
  inbox: 'Community management',
  profile: 'Business profile',
};

// Each page's OWN eligibility. They differ per location, so one flag cannot serve
// both. The profile page serves the AI on either, so either flag qualifies.
function eligibleForTab(tab: SocialTab, a: { canPost: boolean; canInbox: boolean }): boolean {
  return tab === 'posts' ? a.canPost : tab === 'inbox' ? a.canInbox : a.canPost || a.canInbox;
}

// ⚠️ Each page DECLARES its width. Community management is a multi-column app pane:
// `fill` bounds the page to the viewport so the columns scroll inside it (one
// scrollbar each, not a page scrollbar plus a sticky panel). Posting is a scrolling
// composer: `wide`, never fill. The profile page is prose-shaped and keeps the
// default reading column.
const SHELL_WIDTH: Record<SocialTab, { fill?: boolean; wide?: boolean }> = {
  posts: { wide: true },
  inbox: { fill: true },
  profile: {},
};

export function SocialFrame({
  tab,
  children,
}: {
  tab: SocialTab;
  children: (accountId: string) => ReactNode;
}) {
  const [params, setParams] = useSearchParams();
  const me = useQuery({ queryKey: ['portal-me'], queryFn: fetchPortalMe, retry: false });
  const permitted = me.data ? me.data.permissions.includes('social') : null;
  // ⚠️ The server-served list is already intersected with the viewer's scope and
  // carries canPost / canInbox; the page uses ITS OWN flag. Never the whole scope
  // list from /me: that includes locations that cannot use this page. Only runs once
  // the viewer is known to hold the social permission.
  const served = useQuery({
    queryKey: ['portal-social-accounts'],
    queryFn: socialApi.accounts,
    enabled: permitted === true,
    retry: false,
  });
  const options = (served.data?.accounts ?? []).filter((a) => eligibleForTab(tab, a));
  const requested = params.get('account');
  // ⚠️ The left nav cannot carry ?account=, so the choice is remembered across the
  // social pages (see socialAccountMemory.ts). Validated against `options` exactly
  // as the URL value is.
  const accountId = resolveSocialAccount(
    requested,
    loadRememberedSocialAccount(),
    options.map((o) => o.id),
  );

  useEffect(() => {
    if (!accountId) return;
    rememberSocialAccount(accountId);
    // Keep the URL honest (links, bookmarks, the composer's own ?account=).
    if (requested !== accountId) {
      const next = new URLSearchParams(params);
      next.set('account', accountId);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, requested]);

  return (
    <PortalShell signedInAs={firebaseAuth.currentUser?.email ?? null} nav {...SHELL_WIDTH[tab]}>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">{TITLES[tab]}</h1>
      {me.isPending || (permitted === true && served.isPending) ? (
        <CenteredSpinner />
      ) : permitted === false ? (
        <p className="mt-3 max-w-prose text-sm text-muted-foreground" data-testid="social-not-enabled">
          KyckSocial is not switched on for your account. Ask your Kyck account manager to turn it on.
        </p>
      ) : !accountId ? (
        <p className="mt-3 max-w-prose text-sm text-muted-foreground" data-testid="social-none-eligible">
          None of your locations have {TITLES[tab]} switched on yet. Ask your Kyck account manager to turn it on.
        </p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-center justify-end gap-3 border-b border-border pb-2">
            {options.length > 1 && (
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                Location
                <select
                  className="rounded-md border border-input bg-background px-2 py-1 text-foreground"
                  value={accountId}
                  onChange={(e) => {
                    const next = new URLSearchParams(params);
                    next.set('account', e.target.value);
                    setParams(next, { replace: true });
                  }}
                >
                  {options.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <div className={tab === 'inbox' ? 'mt-5 flex min-h-0 flex-1 flex-col' : 'mt-5'}>{children(accountId)}</div>
        </>
      )}
    </PortalShell>
  );
}
